/**
 * Analytics Service
 *
 * Centralized analytics service that manages both Segment and Google Analytics.
 * All tracking is gated behind cookie consent.
 *
 * Usage:
 * - Call initializeAnalytics() on app startup if consent exists
 * - Call track(), identify(), page() methods throughout app
 * - All methods check consent before executing
 * - Call resetAnalytics() when user declines consent
 */

import { AnalyticsBrowser } from '@segment/analytics-next';
import ReactGA from 'react-ga4';
import { hasConsent } from '../utils/cookieConsent';
import { loadGTM, removeGTM } from './gtm';
import { getCampaignData } from '../utils/campaignCapture';

// Singleton instance of Segment analytics
let segmentAnalytics = null;
let isGAInitialized = false;
let isSegmentInitialized = false;
let analyticsInitPromise = null;
let lastTrackedPageKey = null;
let lastTrackedPageTime = 0;

/**
 * Initialize Segment Analytics
 * Returns a Promise that resolves when Segment is ready
 */
async function initializeSegment() {
  if (isSegmentInitialized || !hasConsent()) {
    return false;
  }

  const segmentWriteKey = process.env.REACT_APP_SEGMENT_WRITE_KEY;

  if (!segmentWriteKey) {
    console.warn('Segment write key not configured. Set REACT_APP_SEGMENT_WRITE_KEY in .env');
    return false;
  }

  try {
    // Initialize Segment Analytics Browser
    const [analytics] = await AnalyticsBrowser.load({ writeKey: segmentWriteKey });
    segmentAnalytics = analytics;
    isSegmentInitialized = true;
    return true;
  } catch (error) {
    console.error('❌ Failed to initialize Segment:', error);
    return false;
  }
}

/**
 * Initialize Google Analytics
 */
function initializeGA() {
  if (isGAInitialized || !hasConsent()) {
    return false;
  }

  try {
    ReactGA.initialize('G-LWES1S3VQV');
    isGAInitialized = true;
    return true;
  } catch (error) {
    console.error('Failed to initialize Google Analytics:', error);
    return false;
  }
}

/**
 * Initialize all analytics services
 * Only initializes if user has given consent
 * Returns a Promise that resolves when initialization is complete
 */
export async function initializeAnalytics() {
  if (!hasConsent()) {
    return false;
  }

  if (analyticsInitPromise) {
    return analyticsInitPromise;
  }

  analyticsInitPromise = (async () => {
    try {
      const segmentInit = await initializeSegment();
      const gaInit = initializeGA();

      // Always load GTM after consent in all environments
      const gtmId = 'GTM-5ZBKQVN7';
      loadGTM(gtmId);

      return segmentInit || gaInit;
    } finally {
      analyticsInitPromise = null;
    }
  })();

  return await analyticsInitPromise;
}

/**
 * Track an event
 * Sends to both Segment and GA4 (if initialized and consent given)
 * Automatically includes campaign attribution data from localStorage
 *
 * @param {string} eventName - Name of the event
 * @param {object} properties - Event properties/attributes
 */
export function track(eventName, properties = {}) {
  if (!hasConsent()) {
    return;
  }

  // Get campaign data and merge with properties
  const campaignData = getCampaignData();
  const mergedProperties = {
    ...properties,
    ...(campaignData || {}),
  };

  // Build Segment context with campaign info
  const context = {};
  if (campaignData && (campaignData.utm_source || campaignData.utm_campaign)) {
    const campaignInfo = {
      source: campaignData.utm_source,
      medium: campaignData.utm_medium,
      name: campaignData.utm_campaign,
      term: campaignData.utm_term,
      content: campaignData.utm_content,
    };
    // Remove null/undefined values
    context.campaign = Object.keys(campaignInfo).reduce((acc, key) => {
      if (campaignInfo[key]) {
        acc[key] = campaignInfo[key];
      }
      return acc;
    }, {});
  }

  // Send to Segment
  if (segmentAnalytics && isSegmentInitialized) {
    try {

      const options = Object.keys(context).length > 0 ? { context } : undefined;
      segmentAnalytics.track(eventName, mergedProperties, options);
    } catch (error) {
      console.error('❌ Segment track error:', error);
    }
  } else {
    console.warn('⚠️ Segment not ready. segmentAnalytics:', !!segmentAnalytics, 'isInitialized:', isSegmentInitialized);
  }

  // Send to GA4
  if (isGAInitialized) {
    try {
      ReactGA.event({
        category: properties.category || 'General',
        action: eventName,
        label: properties.label,
        value: properties.value,
        ...mergedProperties,
      });
    } catch (error) {
      console.error('GA4 track error:', error);
    }
  }
}

/**
 * Identify a user
 * Associates user ID and traits with all future events
 * Automatically includes campaign attribution data
 *
 * @param {string} userId - Unique user identifier (typically email)
 * @param {object} traits - User attributes/properties
 */
export function identify(userId, traits = {}) {
  if (!hasConsent()) {
    return;
  }

  // Get campaign data and merge with traits
  const campaignData = getCampaignData();
  const mergedTraits = {
    ...traits,
    ...(campaignData || {}),
  };

  // Send to Segment
  if (segmentAnalytics && isSegmentInitialized) {
    try {
      segmentAnalytics.identify(userId, mergedTraits);
    } catch (error) {
      console.error('❌ Segment identify error:', error);
    }
  }

  // GA4 doesn't have a direct identify method, but we can set user properties
  if (isGAInitialized) {
    try {
      ReactGA.set({ userId });
    } catch (error) {
      console.error('GA4 set user error:', error);
    }
  }
}

/**
 * Track a page view
 * Records when user navigates to a new page
 * Automatically includes campaign attribution data
 *
 * @param {string} name - Page name (optional)
 * @param {object} properties - Page properties (optional)
 */
export function page(name, properties = {}) {
  if (!hasConsent()) {
    return;
  }

  const pagePath = properties.path || window.location.pathname;
  const pageTitle = name || document.title;
  const canSendSegment = segmentAnalytics && isSegmentInitialized;
  const canSendGA = isGAInitialized;

  if (!canSendSegment && !canSendGA) {
    return;
  }

  // Get campaign data and merge with properties
  const campaignData = getCampaignData();
  const mergedProperties = {
    ...properties,
    ...(campaignData || {}),
  };

  const now = Date.now();
  const dedupeKey = `${name || ''}|${mergedProperties.path || pagePath}`;

  if (dedupeKey === lastTrackedPageKey && now - lastTrackedPageTime < 1500) {
    return;
  }

  lastTrackedPageKey = dedupeKey;
  lastTrackedPageTime = now;

  // Send to Segment
  if (canSendSegment) {
    try {
      segmentAnalytics.page(name, mergedProperties);
    } catch (error) {
      console.error('❌ Segment page error:', error);
    }
  }

  // Send to GA4
  if (canSendGA) {
    try {
      ReactGA.send({
        hitType: 'pageview',
        page: pagePath,
        title: pageTitle,
      });
    } catch (error) {
      console.error('GA4 pageview error:', error);
    }
  }
}

/**
 * Reset analytics (clear user identity and stop tracking)
 * Call this when user declines consent or revokes it
 */
export function resetAnalytics() {
  // Reset Segment
  if (segmentAnalytics && isSegmentInitialized) {
    try {
      segmentAnalytics.reset();
    } catch (error) {
      console.error('Segment reset error:', error);
    }
  }

  // Clear Segment cookies manually
  try {
    // Segment uses these cookie names
    const segmentCookies = ['ajs_user_id', 'ajs_group_id', 'ajs_anonymous_id'];
    segmentCookies.forEach((cookieName) => {
      document.cookie = `${cookieName}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`;
    });
  } catch (error) {
    console.error('Failed to clear Segment cookies:', error);
  }

  // Clear GA4 cookies
  try {
    // GA4 uses cookies starting with _ga
    const cookies = document.cookie.split(';');
    cookies.forEach((cookie) => {
      const cookieName = cookie.split('=')[0].trim();
      if (cookieName.startsWith('_ga')) {
        document.cookie = `${cookieName}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`;
      }
    });
  } catch (error) {
    console.error('Failed to clear GA4 cookies:', error);
  }

  // Clean up GTM
  try {
    removeGTM();
  } catch (error) {
    console.error('Failed to remove GTM:', error);
  }

  // Reset initialization flags
  isSegmentInitialized = false;
  isGAInitialized = false;
  segmentAnalytics = null;
  analyticsInitPromise = null;

}

/**
 * Check if analytics is initialized
 */
export function isAnalyticsInitialized() {
  return isSegmentInitialized || isGAInitialized;
}
